import { useCallback, useEffect, useState } from "react";
import { scheduleApi } from "./schedule.api";
import { WeeklySchedule, validateWeeklySchedule } from "@/models/individual-schedule.model";
import { useAppSelector } from "@/lib/store";

export const useIndividualSchedule = () => {
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const contract = useAppSelector((state) => state.organization.activeOrgId);

  useEffect(() => {
    if (!contract) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    scheduleApi
      .getSchedule(contract)
      .then((schedule) => {
        if (mounted) setWeeklySchedule(schedule);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : "Failed to load schedule");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [contract, attempt]);

  const saveSchedule = useCallback(
    async (schedule: WeeklySchedule) => {
      if (!contract) throw new Error("No active organization");
      if (!validateWeeklySchedule(schedule)) {
        throw new Error("Invalid schedule: Please check all enabled days have valid start and end times");
      }
      setSaving(true);
      try {
        setWeeklySchedule(await scheduleApi.saveSchedule(contract, schedule));
      } finally {
        setSaving(false);
      }
    },
    [contract]
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { weeklySchedule, loading, saving, error, saveSchedule, retry };
};
